import { useState, useEffect, useCallback } from 'react'
import { getEspecialistas, createEspecialista, updateEspecialista, deleteEspecialista, registrarConsulta } from '@/services/api'
import { formatDate } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Plus, Stethoscope, Trash2, Edit, Loader2, UserPlus, Calendar, Users } from 'lucide-react'
import { toast } from 'sonner'

export default function EspecialistasPage() {
  const [especialistas, setEspecialistas] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingEspecialista, setEditingEspecialista] = useState(null)
  const [formData, setFormData] = useState({
    nome_medico: '',
    especialidade: '',
    dia_atendimento: '',
    quantidade_pacientes: ''
  })

  const fetchEspecialistas = useCallback(async () => {
    try {
      const response = await getEspecialistas()
      setEspecialistas(response.data)
    } catch (error) {
      toast.error('Erro ao carregar especialistas')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEspecialistas()
  }, [fetchEspecialistas])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const data = {
        ...formData,
        quantidade_pacientes: parseInt(formData.quantidade_pacientes)
      }
      
      if (editingEspecialista) {
        await updateEspecialista(editingEspecialista.id, data)
        toast.success('Especialista atualizado com sucesso!')
      } else {
        await createEspecialista(data)
        toast.success('Especialista criado com sucesso!')
      }
      
      setDialogOpen(false)
      setEditingEspecialista(null)
      setFormData({ nome_medico: '', especialidade: '', dia_atendimento: '', quantidade_pacientes: '' })
      fetchEspecialistas()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar especialista')
    }
  }

  const handleEdit = (especialista) => {
    setEditingEspecialista(especialista)
    setFormData({
      nome_medico: especialista.nome_medico,
      especialidade: especialista.especialidade,
      dia_atendimento: especialista.dia_atendimento.split('T')[0],
      quantidade_pacientes: especialista.quantidade_pacientes.toString()
    })
    setDialogOpen(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este especialista?')) return
    try {
      await deleteEspecialista(id)
      toast.success('Especialista excluído com sucesso!')
      fetchEspecialistas()
    } catch (error) {
      toast.error('Erro ao excluir especialista')
    }
  }

  const handleRegistrarConsulta = async (id) => {
    try {
      await registrarConsulta(id)
      toast.success('Consulta registrada com sucesso!')
      fetchEspecialistas()
    } catch (error) {
      toast.error('Erro ao registrar consulta')
    }
  }

  const totalConsultas = especialistas.reduce((a, e) => a + e.consultas_realizadas, 0)
  const totalPacientes = especialistas.reduce((a, e) => a + e.quantidade_pacientes, 0)

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Especialistas</h1>
          <p className="text-slate-500 mt-1">Gerencie os agendamentos de especialistas</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="novo-especialista-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Agendamento
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingEspecialista ? 'Editar Agendamento' : 'Novo Agendamento'}</DialogTitle>
              <DialogDescription>
                Preencha os dados do agendamento
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome_medico">Nome do Médico</Label>
                <Input
                  id="nome_medico"
                  value={formData.nome_medico}
                  onChange={(e) => setFormData({ ...formData, nome_medico: e.target.value })}
                  required
                  data-testid="input-nome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="especialidade">Especialidade</Label>
                <Input
                  id="especialidade"
                  value={formData.especialidade}
                  onChange={(e) => setFormData({ ...formData, especialidade: e.target.value })}
                  required
                  placeholder="Ex: Cardiologia, Ortopedia"
                  data-testid="input-especialidade"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dia_atendimento">Dia do Atendimento</Label>
                <Input
                  id="dia_atendimento"
                  type="date"
                  value={formData.dia_atendimento}
                  onChange={(e) => setFormData({ ...formData, dia_atendimento: e.target.value })}
                  required
                  data-testid="input-dia"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quantidade_pacientes">Quantidade de Pacientes</Label>
                <Input
                  id="quantidade_pacientes"
                  type="number"
                  min="1"
                  value={formData.quantidade_pacientes}
                  onChange={(e) => setFormData({ ...formData, quantidade_pacientes: e.target.value })}
                  required
                  data-testid="input-quantidade"
                />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="salvar-especialista-btn">
                  {editingEspecialista ? 'Atualizar' : 'Criar'} Agendamento
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total de Agendamentos</p>
                <p className="text-2xl font-semibold text-slate-900">{especialistas.length}</p>
              </div>
              <Stethoscope className="w-8 h-8 text-teal-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Consultas Realizadas</p>
                <p className="text-2xl font-semibold text-emerald-600">{totalConsultas}</p>
              </div>
              <Calendar className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pacientes Agendados</p>
                <p className="text-2xl font-semibold text-blue-600">{totalPacientes}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : especialistas.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Stethoscope className="w-12 h-12 mb-4 text-slate-300" />
              <p>Nenhum agendamento encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Médico</TableHead>
                  <TableHead>Especialidade</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Progresso</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {especialistas.map((esp) => {
                  const progress = esp.quantidade_pacientes > 0 
                    ? Math.round((esp.consultas_realizadas / esp.quantidade_pacientes) * 100) 
                    : 0
                  return (
                    <TableRow key={esp.id} className="hover:bg-slate-50" data-testid={`especialista-row-${esp.id}`}>
                      <TableCell className="font-medium">{esp.nome_medico}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                          {esp.especialidade}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(esp.dia_atendimento)}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">{esp.consultas_realizadas}/{esp.quantidade_pacientes}</span>
                            <span className="text-slate-500">{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => handleRegistrarConsulta(esp.id)}
                            disabled={esp.consultas_realizadas >= esp.quantidade_pacientes}
                            data-testid={`btn-consulta-${esp.id}`}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(esp)} data-testid={`btn-edit-${esp.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => handleDelete(esp.id)} data-testid={`btn-delete-${esp.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
